import type { BaseQueryApi } from "@reduxjs/toolkit/query";
import type {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from "@reduxjs/toolkit/query";
import { handleLogout, refresh } from "../../../features/auth/authSlice";
import { enqueueSnackbar } from "../../../features/notifier/notifierSlice";
import type { RootState } from "../../store";

const parseCreativeManagerParams = (headerValue: string) => {
  const trimmedValue = headerValue.trim();
  if (!trimmedValue) {
    return null;
  }

  const parseCandidates = [trimmedValue];

  try {
    parseCandidates.push(decodeURIComponent(trimmedValue));
  } catch {
    // ignore URI decode failures and fallback to raw header value
  }

  for (const candidate of parseCandidates) {
    try {
      const parsed = JSON.parse(candidate) as Record<string, unknown>;
      return parsed;
    } catch {
      // continue trying remaining candidates
    }
  }

  return null;
};

const getCreativeManagerDetail = (parsed: Record<string, unknown> | null) => {
  if (!parsed) {
    return "";
  }

  const detail = parsed.detail;
  if (typeof detail === "string" && detail.trim()) {
    return detail;
  }

  const message = parsed.message;
  if (typeof message === "string" && message.trim()) {
    return message;
  }

  return "";
};

export const baseQueryWithReauth =
  <
    BaseQuery extends BaseQueryFn<
      string | FetchArgs,
      unknown,
      FetchBaseQueryError
    >,
  >(
    baseQuery: BaseQuery,
  ) =>
  async (
    args: Parameters<BaseQuery>[0],
    api: BaseQueryApi,
    extraOptions: Parameters<BaseQuery>[2],
  ) => {
    let result = await baseQuery(args, api, extraOptions);
    const response = (result.meta as { response?: Response } | undefined)
      ?.response;

    if (result.error && response) {
      const creativeManagerParamsHeader = response.headers.get(
        "x-creative-manager-params",
      );

      if (creativeManagerParamsHeader) {
        const parsedManagerParams = parseCreativeManagerParams(
          creativeManagerParamsHeader,
        );
        const managerDetail =
          getCreativeManagerDetail(parsedManagerParams) ||
          creativeManagerParamsHeader;

        if (
          typeof result.error.data === "object" &&
          result.error.data !== null
        ) {
          const currentData = result.error.data as Record<string, unknown>;
          result.error.data = {
            ...currentData,
            detail:
              typeof currentData.detail === "string" &&
              currentData.detail.trim()
                ? currentData.detail
                : managerDetail,
            creative_manager_params:
              parsedManagerParams || creativeManagerParamsHeader,
          };
        } else {
          result.error.data = {
            detail: managerDetail,
            creative_manager_params:
              parsedManagerParams || creativeManagerParamsHeader,
          };
        }
      }
    }

    const state = api.getState() as RootState;

    // Check for bad authorization token with proper type checking
    if (
      result.error?.data &&
      typeof result.error.data === "object" &&
      "detail" in result.error.data
    ) {
      const errorData = result.error.data as { detail?: string };
      if (errorData.detail === "Authorization token is bad") {
        api.dispatch(handleLogout());
      }
    }

    if (result.error && result.error.status === 401) {
      // try to get a new token
      if (state.auth.authenticationResult?.RefreshToken) {
        const refreshResult = await api.dispatch(
          refresh({ token: state.auth.authenticationResult.RefreshToken }),
        );

        if (refreshResult.meta.requestStatus === "fulfilled") {
          result = await baseQuery(args, api, extraOptions);
        } else {
          // refresh failed - do something like redirect to login or show a "retry" button
          api.dispatch(handleLogout());
          // Notify user
          api.dispatch(
            enqueueSnackbar({
              message: "Login session expired, logging you out...",
              options: {
                key: "login_session_expired",
                variant: "error",
              },
            }),
          );
        }
      } else {
        // refresh failed - do something like redirect to login or show a "retry" button
        api.dispatch(handleLogout());
        // Notify user
        api.dispatch(
          enqueueSnackbar({
            message: "Login session expired, logging you out...",
            options: {
              key: "login_session_expired",
              variant: "error",
            },
          }),
        );
      }
    }
    return result;
  };
